import React, { useState } from 'react';
import {
  VStack,
  HStack,
  Box,
  Text,
  Image,
  Badge,
  useColorModeValue,
  Card,
  CardBody,
  Flex,
  Spacer,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  IconButton,
  Center,
  useToast,
} from '@chakra-ui/react';
import { CalendarIcon, ViewIcon, EditIcon, DeleteIcon, CheckIcon, InfoIcon } from '@chakra-ui/icons';
import { declarationsAPI } from '../utils/api';
import DeclarationFormModal from './DeclarationFormModal';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';

console.log('🔗 BASE_URL:', BASE_URL);

const DeclarationList = ({ declarations, type, userRole, onRefresh }) => {
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState({});
  const [editingDeclaration, setEditingDeclaration] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentEditType, setCurrentEditType] = useState(null);
  const toast = useToast();

  const handleImageClick = (imageUrl, declarationType) => {
    setSelectedImage({ url: imageUrl, type: declarationType });
    onOpen();
  };

  const handleEdit = (declaration) => {

    setEditingDeclaration(declaration);
    setCurrentEditType(declaration.type);
    setIsEditModalOpen(true);
  };

  const handleDeclarationUpdated = (updatedDeclaration) => {
    console.log('updatedDeclaration', updatedDeclaration);
    // Update the editingDeclaration with the latest data from backend
    setEditingDeclaration(updatedDeclaration);
    // Update the declaration in the list
    if (onRefresh) onRefresh();
  };

  const handleValidate = async (declarationId) => {
    setLoading(prev => ({ ...prev, [declarationId]: true }));
    try {
      await declarationsAPI.validate(declarationId);
      toast({
        title: 'Succès',
        description: 'Déclaration validée avec succès',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error validating declaration:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de valider la déclaration',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(prev => ({ ...prev, [declarationId]: false }));
    }
  };

  const handleDelete = async (declarationId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette déclaration ?')) {
      return;
    }
    
    setLoading(prev => ({ ...prev, [declarationId]: true }));
    try {
      await declarationsAPI.delete(declarationId);
      toast({
        title: 'Succès',
        description: 'Déclaration supprimée avec succès',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error deleting declaration:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de supprimer la déclaration',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(prev => ({ ...prev, [declarationId]: false }));
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'cheque': return 'Chèque';
      case 'effet': return 'Effet';
      case 'espece': return 'Espèce';
      case 'virement': return 'Virement';
      case 'compte client': return 'Compte Client';
      case 'versement deplacer': return 'Versement Déplacé';
      default: return type;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'cheque': return 'blue';
      case 'effet': return 'green';
      case 'espece': return 'orange';
      case 'virement': return 'purple';
      case 'compte client': return 'teal';
      case 'versement deplacer': return 'pink';
      default: return 'gray';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'MAD',
    }).format(amount);
  };

  if (declarations.length === 0) {
    return (
      <Box
        textAlign="center"
        py={12}
        bg={bg}
        borderRadius="lg"
        border="1px solid"
        borderColor={borderColor}
      >
        <Text color="gray.500" fontSize="lg">
          Aucune déclaration de type "{getTypeLabel(type)}" trouvée
        </Text>
        <Text color="gray.400" fontSize="sm" mt={2}>
          Ajoutez votre première déclaration en utilisant le bouton ci-dessus
        </Text>
      </Box>
    );
  }

  return (
    <>
      <VStack spacing={4} align="stretch">

        {/* Summary Cards */}
        <Box>
          <Flex 
            direction={{ base: 'column', md: 'row' }} 
            gap={4} 
            mb={6}
            justify="space-between"
          >
            {/* Total Amount Card */}
            <Card
              bg="blue.50"
              border="1px solid"
              borderColor="blue.200"
              flex="1"
              maxW={{ base: 'full', md: '300px' }}
              _dark={{ bg: 'blue.900', borderColor: 'blue.700' }}
            >
              <CardBody p={4}>
                <VStack spacing={2} align="center">
                  <HStack spacing={2}>
                    <Icon as={InfoIcon} color="blue.500" boxSize={5} />
                    <Text fontSize="sm" fontWeight="medium" color="blue.600" _dark={{ color: 'blue.300' }}>
                      Montant Total
                    </Text>
                  </HStack>
                  <Text fontSize="2xl" fontWeight="bold" color="blue.700" _dark={{ color: 'blue.200' }}>
                    {formatAmount(declarations.reduce((sum, declaration) => sum + ( parseFloat(declaration.montant) || 0), 0))} MAD
                  </Text>
                  <Text fontSize="xs" color="blue.500" _dark={{ color: 'blue.400' }}>
                    {declarations.length} déclaration{declarations.length > 1 ? 's' : ''}
                  </Text>
                </VStack>
              </CardBody>
            </Card>

            {/* Allocated Amount Card */}
            {/* <Card
              bg="green.50"
              border="1px solid"
              borderColor="green.200"
              flex="1"
              maxW={{ base: 'full', md: '300px' }}
              _dark={{ bg: 'green.900', borderColor: 'green.700' }}
            >
              <CardBody p={4}>
                <VStack spacing={2} align="center">
                  <HStack spacing={2}>
                    <Icon as={CheckIcon} color="green.500" boxSize={5} />
                    <Text fontSize="sm" fontWeight="medium" color="green.600" _dark={{ color: 'green.300' }}>
                      Montant Alloué
                    </Text>
                  </HStack>
                  <Text fontSize="2xl" fontWeight="bold" color="green.700" _dark={{ color: 'green.200' }}>
                    {formatAmount(declarations.reduce((sum, declaration) => sum + ( parseFloat(declaration.montant_allouer) || 0), 0))} MAD
                  </Text>
                  <Text fontSize="xs" color="green.500" _dark={{ color: 'green.400' }}>
                    {declarations.filter(d => d.montant_allouer && d.montant_allouer > 0).length} avec allocation
                  </Text>
                </VStack>
              </CardBody>
            </Card> */}

            {/* Remaining Amount Card */}
            {/* <Card
              bg="orange.50"
              border="1px solid"
              borderColor="orange.200"
              flex="1"
              maxW={{ base: 'full', md: '300px' }}
              _dark={{ bg: 'orange.900', borderColor: 'orange.700' }}
            >
              <CardBody p={4}>
                <VStack spacing={2} align="center">
                  <HStack spacing={2}>
                    <Icon as={CalendarIcon} color="orange.500" boxSize={5} />
                    <Text fontSize="sm" fontWeight="medium" color="orange.600" _dark={{ color: 'orange.300' }}>
                      Montant Restant
                    </Text>
                  </HStack>
                  <Text fontSize="2xl" fontWeight="bold" color="orange.700" _dark={{ color: 'orange.200' }}>
                    {formatAmount(
                      declarations.reduce((sum, declaration) => sum + ( parseFloat(declaration.montant) || 0), 0) - 
                      declarations.reduce((sum, declaration) => sum + ( parseFloat(declaration.montant_allouer) || 0), 0)
                    )} MAD
                  </Text>
                  <Text fontSize="xs" color="orange.500" _dark={{ color: 'orange.400' }}>
                    En attente d'allocation
                  </Text>
                </VStack>
              </CardBody>
            </Card> */}
          </Flex>
        </Box>
      

        {declarations.map((declaration) => (
          <Card
            key={declaration.id}
            bg={bg}
            border="1px solid"
            borderColor={borderColor}
            shadow="sm"
            _hover={{ shadow: 'md' }}
            transition="all 0.2s"
          >
            <CardBody p={4}>


            
              <Flex direction={{ base: 'column', md: 'row' }} gap={4}>
                {/* Enhanced Thumbnail - for all types with photos */}
                {declaration.photo_url && (
                  <Box
                    flexShrink={0}
                    w={{ base: 'full', md: '140px' }}
                    h={{ base: '100px', md: '100px' }}
                    borderRadius="lg"
                    overflow="hidden"
                    bg="gray.100"
                    _dark={{ bg: 'gray.700' }}
                    position="relative"
                    cursor="pointer"
                    onClick={() => handleImageClick(declaration.photo_url, declaration.type)}
                    _hover={{
                      transform: 'scale(1.02)',
                      shadow: 'lg',
                    }}
                    transition="all 0.2s"
                  >
                    <Image
                      src={`${BASE_URL}${declaration.photo_url}`}
                      alt={`Photo ${getTypeLabel(declaration.type)}`}
                      w="full"
                      h="full"
                      objectFit="cover"
                      fallback={
                        <Box
                          w="full"
                          h="full"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          bg="gray.200"
                          _dark={{ bg: 'gray.600' }}
                        >
                          <Text fontSize="xs" color="gray.500">
                            {getTypeLabel(declaration.type)}
                          </Text>
                        </Box>
                      }
                    />
                    {/* Overlay with view icon */}
                    <Box
                      position="absolute"
                      top={0}
                      left={0}
                      right={0}
                      bottom={0}
                      bg="blackAlpha.400"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      opacity={0}
                      _hover={{ opacity: 1 }}
                      transition="opacity 0.2s"
                    >
                      <IconButton
                        icon={<ViewIcon />}
                        colorScheme="whiteAlpha"
                        variant="solid"
                        size="sm"
                        aria-label="Voir l'image"
                      />
                    </Box>
                  </Box>
                )}

                {/* Content */}
                <Box flex="1" minW={0}>
                  <Flex direction="column" h="full" justify="space-between">
                    <Box>
                      <HStack mb={2}>
                        <Badge colorScheme={getTypeColor(declaration.type)} variant="subtle">
                          {getTypeLabel(declaration.type)}
                        </Badge>
                        {declaration.is_valid === 1 && (
                          <Badge colorScheme="green" variant="solid">
                            Validé
                          </Badge>
                        )}
                        {declaration.is_valid === 0 && userRole === 'Commercial' && (
                          <Badge colorScheme="orange" variant="solid">
                            À valider
                          </Badge>
                        )}
                        <Spacer />
                        <Text fontSize="lg" fontWeight="bold" color="brand.500">
                          {formatAmount(declaration.montant)}
                        </Text>
                      </HStack>

                      {declaration.description && (
                        <Text fontSize="sm" color="gray.600" _dark={{ color: 'gray.400' }} mb={2}>
                          {declaration.description}
                        </Text>
                      )}

                      {/* Associated Documents Display */}
                      {declaration.declaration_details && declaration.declaration_details.length > 0 && (
                        <Box mb={2}>
                          <Text fontSize="xs" fontWeight="medium" color="gray.600" mb={1}>
                            Documents associés:
                          </Text>
                          <VStack spacing={1} align="stretch">
                            {declaration.declaration_details.map((detail, index) => (
                              <HStack key={index} spacing={2} p={1} bg="blue.50" borderRadius="sm">
                                <Badge size="xs" colorScheme={
                                  detail.type === 'facture' ? 'blue' : 
                                  detail.type === 'devis' ? 'green' : 'purple'
                                }>
                                  {detail.type === 'facture' ? 'Facture' : 
                                   detail.type === 'devis' ? 'Devis' : 'Proforma'}
                                </Badge>
                                <Text fontSize="xs" color="gray.600">
                                  {detail.documentInfo ? 
                                    (detail.documentInfo.display_name || 
                                     (detail.type === 'facture' ? detail.documentInfo.Num_facture :
                                      detail.type === 'devis' ? detail.documentInfo.Num_devis :
                                      detail.documentInfo.Num_facture)) : detail.uuid_related}
                                </Text>
                                <Text fontSize="xs" color="gray.600">
                                  {detail.documentInfo ? detail.documentInfo.total_amount : 0} MAD
                                </Text>
                                {detail.documentInfo && detail.documentInfo.date && (
                                  <>
                                  <Text fontSize="xs" color="gray.400">
                                    {detail.documentInfo.date}
                                  </Text>
                                  <Text fontSize="xs" color="gray.400">
                                    {detail.documentInfo.nom_client}
                                  </Text>
                                  </>
                                )}
                              </HStack>
                            ))}
                          </VStack>
                        </Box>
                      )}

                      <HStack spacing={4} fontSize="xs" color="gray.500">
                        {declaration.date_valeur && (
                          <HStack spacing={1}>
                            <Icon as={CalendarIcon} />
                            <Text>Date de valeur: {formatDate(declaration.date_valeur)}</Text>
                          </HStack>
                        )}
                        <HStack spacing={1}>
                          <Icon as={CalendarIcon} />
                          <Text>Créé le: {formatDate(declaration.created_at)}</Text>
                        </HStack>
                      </HStack>
                    </Box>

                    {/* Action buttons */}
                    
                    <HStack spacing={2} mt={4}>
                      
                      <IconButton
                        icon={<ViewIcon />}
                        onClick={() => handleImageClick(declaration.photo_url, declaration.type)}
                        colorScheme="blue"
                        size="sm"
                        variant="outline"
                        aria-label="Voir la photo"
                      />
                     { ( userRole === 'Technico' || declaration.is_valid === 0 || declaration.type == 'compte client' || declaration.type == 'note de frais'  )  &&  (  <IconButton
                        icon={<EditIcon />}
                        onClick={() => handleEdit(declaration)}
                        colorScheme="green"
                        size="sm"
                        variant="outline"
                        aria-label="Modifier"
                      /> )}
                      {/* Only show delete button for Technico and not for compte client */}
                      {userRole === 'Technico' && declaration.type !== 'compte client' ||  (declaration.type == "compte client") || (declaration.type == 'note de frais') && (
                        <IconButton
                          icon={<DeleteIcon />}
                          onClick={() => handleDelete(declaration.id)}
                          colorScheme="red"
                          size="sm"
                          variant="outline"
                          aria-label="Supprimer"
                          isLoading={loading[declaration.id]}
                        />
                      )}
                      {/* Show validate button for Commercial */}
                      {userRole === 'Commercial' && declaration.is_valid === 0 && (
                        <IconButton
                          icon={<CheckIcon />}
                          onClick={() => handleValidate(declaration.id)}
                          colorScheme="green"
                          size="sm"
                          variant="outline"
                          aria-label="Valider"
                          isLoading={loading[declaration.id]}
                        />
                      )}
                    </HStack>
                  </Flex>
                </Box>
              </Flex>
            </CardBody>
          </Card>
        ))}
      </VStack>

      {/* Image Preview Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent maxW="90vw" maxH="90vh">
          <ModalHeader>
            Aperçu - {selectedImage && getTypeLabel(selectedImage.type)}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody p={0}>
            {selectedImage && (
              <Center>
                <Image
                  src={`${BASE_URL}${selectedImage.url}`}
                  alt={`Aperçu ${getTypeLabel(selectedImage.type)}`}
                  maxW="100%"
                  maxH="70vh"
                  objectFit="contain"
                  fallback={
                    <Box
                      w="400px"
                      h="300px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      bg="gray.200"
                      _dark={{ bg: 'gray.600' }}
                      borderRadius="md"
                    >
                      <Text color="gray.500">
                        Impossible de charger l'image
                      </Text>
                    </Box>
                  }
                />
              </Center>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Edit Declaration Modal */}
      <DeclarationFormModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingDeclaration(null);
          setCurrentEditType(null);
        }}
        type={currentEditType || editingDeclaration?.type || type}
        onDeclarationUpdated={handleDeclarationUpdated}
        userRole={userRole}
        editingDeclaration={editingDeclaration}
        onTypeChange={setCurrentEditType}
      />
    </>
  );
};

export default DeclarationList; 